import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { RouterLink } from '@angular/router';
import { Showtime } from '../models';
import { LocationFilterService } from '../services/location-filter.service';

@Component({
  selector: 'app-showtime-result-row',
  standalone: true,
  imports: [CommonModule, RouterLink],
  template: `
    <article class="showtime-row" *ngIf="showtime">
      <a class="poster-thumb" *ngIf="showtime.movie" [routerLink]="['/movies', showtime.movie.id]">
        <img *ngIf="showtime.movie.posterUrl" [src]="showtime.movie.posterUrl" [alt]="showtime.movie.title + ' poster'" loading="lazy" />
        <span *ngIf="!showtime.movie.posterUrl">{{ initials(showtime.movie.title) }}</span>
      </a>
      <div class="showtime-main">
        <a class="title-link" *ngIf="showtime.movie" [routerLink]="['/movies', showtime.movie.id]">{{ showtime.movie.title }}</a>
        <div class="venue-line">
          <a *ngIf="showtime.cinema" [routerLink]="['/cinemas', showtime.cinema.id]">{{ showtime.cinema.name }}</a>
          <span>{{ distanceLabel() || showtime.cinema?.city || showtime.cinema?.county }}</span>
        </div>
        <div class="meta-tags">
          <span>{{ showtime.showDate | date:'EEE d MMM' }}</span>
          <span *ngIf="showtime.movie?.language">{{ showtime.movie?.language }}</span>
          <span *ngIf="showtime.screenType || showtime.format">{{ showtime.screenType || showtime.format }}</span>
        </div>
      </div>
      <a class="time-cta" [href]="showtime.redirectUrl" target="_blank" rel="noreferrer">{{ showtime.showTime }}</a>
    </article>
  `,
  styles: [
    `
      .showtime-row {
        display: grid;
        grid-template-columns: 54px minmax(0, 1fr) 72px;
        gap: 10px;
        align-items: center;
        min-height: 76px;
        padding: 8px;
        border: 1px solid var(--line);
        border-radius: 16px;
        background: var(--surface-row);
        box-shadow: 0 10px 28px rgba(0,0,0,.18);
      }

      .poster-thumb {
        width: 54px;
        height: 60px;
        border-radius: 12px;
        overflow: hidden;
        display: grid;
        place-items: center;
        color: var(--text);
        background: linear-gradient(135deg, var(--blue), var(--violet));
        font-weight: 950;
      }

      .poster-thumb img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .showtime-main {
        min-width: 0;
        display: grid;
        gap: 4px;
      }

      .title-link {
        color: var(--text);
        font-size: 15px;
        line-height: 1.15;
        font-weight: 950;
        overflow-wrap: anywhere;
      }

      .venue-line {
        display: flex;
        min-width: 0;
        gap: 6px;
        color: var(--muted);
        font-size: 12px;
        font-weight: 750;
      }

      .venue-line a {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        color: var(--muted);
      }

      .venue-line span {
        flex: 0 0 auto;
        color: var(--faint);
      }

      .meta-tags {
        display: flex;
        flex-wrap: wrap;
        gap: 5px;
      }

      .meta-tags span {
        min-height: 22px;
        border-radius: 999px;
        padding: 4px 7px;
        background: var(--control-bg);
        color: var(--faint);
        font-size: 11px;
        font-weight: 850;
      }

      .time-cta {
        min-height: 46px;
        border-radius: 14px;
        display: grid;
        place-items: center;
        color: var(--accent-contrast);
        background: var(--cyan);
        font-size: 15px;
        font-weight: 950;
        box-shadow: 0 8px 22px rgba(88,224,255,.16);
      }

      @media (max-width: 640px) {
        .showtime-row {
          grid-template-columns: 48px minmax(0, 1fr) 66px;
          min-height: 72px;
          gap: 8px;
          padding: 7px;
          border-radius: 14px;
        }

        .poster-thumb {
          width: 48px;
          height: 56px;
          border-radius: 11px;
          font-size: 13px;
        }

        .title-link {
          font-size: 14px;
        }

        .time-cta {
          min-height: 44px;
          border-radius: 12px;
          font-size: 14px;
        }

        .meta-tags span:nth-child(n+3) {
          display: none;
        }
      }

      @media (max-width: 380px) {
        .showtime-row {
          grid-template-columns: 42px minmax(0, 1fr) 62px;
        }

        .poster-thumb {
          width: 42px;
          height: 52px;
        }
      }
    `,
  ],
})
export class ShowtimeResultRowComponent {
  @Input({ required: true }) showtime!: Showtime;

  constructor(private readonly location: LocationFilterService) {}

  distanceLabel(): string {
    return this.location.formatDistance(this.showtime.cinema);
  }

  initials(title: string): string {
    return title.split(/\s+/).slice(0, 2).map((word) => word[0]).join('').toUpperCase();
  }
}
